import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { InvoiceFilters, InvoiceTable, GenerateInvoiceForm } from './components';
import { Invoice, backendStatusToUi } from './invoicesTypes';
import { useInvoicesStore } from '../../../store/invoicesStore';
import { Invoice as BackendInvoice } from '../../../services/invoicesApi';
import { formatOrderId } from '../../../utils/formatId';
import styles from './AdminInvoices.module.css';

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function adapt(b: BackendInvoice): Invoice {
  // Compute due date as 30 days after invoice_date (backend doesn't yet have due_date)
  const issued = new Date(b.invoice_date);
  const due = new Date(issued.getTime() + 30 * 24 * 60 * 60 * 1000);
  const overdue = b.status === 'overdue' || (b.status === 'sent' && due.getTime() < Date.now());
  const balance = b.status === 'paid' ? 0 : Number(b.total_amount);
  return {
    id: b.id,
    orderId: b.order_id,
    invoiceNumber: b.invoice_number,
    customer: '—',
    orderNumber: formatOrderId(b.order_id),
    issueDate: formatDate(b.invoice_date),
    dueDate: formatDate(due.toISOString()),
    dueDateOverdue: overdue,
    amount: `$${Number(b.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    balance: `$${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    status: backendStatusToUi(b.status),
  };
}

const AdminInvoices: React.FC = () => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('All Statuses');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const items = useInvoicesStore((s) => s.items);
  const loading = useInvoicesStore((s) => s.loading);
  const error = useInvoicesStore((s) => s.error);
  const setSource = useInvoicesStore((s) => s.setSource);
  const setFilters = useInvoicesStore((s) => s.setFilters);
  const fetchList = useInvoicesStore((s) => s.fetchList);

  const generateRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setSource('admin');
    fetchList();
  }, [setSource, fetchList]);

  // Push status + date filters to the server when changed
  useEffect(() => {
    const backendStatus =
      status === 'Paid' ? 'paid'
      : status === 'Overdue' ? 'overdue'
      : status === 'Draft' ? 'draft'
      : status === 'Partial' ? 'sent'
      : undefined;
    setFilters({ status: backendStatus as any, from_date: dateFrom || undefined, to_date: dateTo || undefined });
    fetchList();
  }, [status, dateFrom, dateTo, setFilters, fetchList]);

  const adapted = useMemo<Invoice[]>(() => items.map(adapt), [items]);

  const filtered = useMemo(() => {
    if (!search.trim()) return adapted;
    const q = search.toLowerCase();
    return adapted.filter(
      (inv) =>
        inv.invoiceNumber.toLowerCase().includes(q) ||
        inv.orderNumber.toLowerCase().includes(q) ||
        inv.customer.toLowerCase().includes(q)
    );
  }, [adapted, search]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <p className={styles.subtitle}>Record and view payment transactions</p>
        <button
          type="button"
          className={styles.generateBtn}
          onClick={() => generateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        >
          + Generate Invoice
        </button>
      </div>

      <div className={styles.tableCard}>
        <InvoiceFilters
          search={search}
          onSearchChange={setSearch}
          status={status}
          onStatusChange={setStatus}
          dateFrom={dateFrom}
          onDateFromChange={setDateFrom}
          dateTo={dateTo}
          onDateToChange={setDateTo}
        />
        <AsyncBoundary
          loading={loading && items.length === 0}
          error={error}
          empty={!loading && filtered.length === 0}
          emptyMessage="No invoices match the current filters"
          onRetry={() => fetchList()}
        >
          <InvoiceTable items={filtered} />
        </AsyncBoundary>
      </div>

      <GenerateInvoiceForm ref={generateRef} onCreated={() => fetchList()} />
    </div>
  );
};

export const AdminInvoicesPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin', 'Customer']}>
    <AdminInvoices />
  </ProtectedRoute>
);

export { AdminInvoices };
export default AdminInvoices;
